import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MessageSquare, Send, ArrowLeft, Search, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { formatTime } from '../lib/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 8000;

const Bubble = ({ msg, isMine }) => (
  <div className={`flex ${isMine ? 'justify-end' : 'justify-start'}`} data-testid={`chat-message-${msg._id}`}>
    <div
      className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm shadow-sm ${
        isMine
          ? 'bg-[#2563eb] text-white rounded-br-sm'
          : 'bg-white text-[#0f172a] border border-[#e2e8f0] rounded-bl-sm'
      }`}
    >
      {!isMine && (
        <p className="text-[10px] font-semibold uppercase tracking-widest opacity-70 mb-0.5">
          {msg.sender_name}{msg.sender_role === 'management' && ' · Management'}
        </p>
      )}
      <p className="whitespace-pre-wrap break-words leading-relaxed">{msg.content}</p>
      <p className={`text-[10px] mt-1 ${isMine ? 'text-white/70' : 'text-[#94a3b8]'}`}>
        {formatTime(msg.created_at)}
      </p>
    </div>
  </div>
);

const Composer = ({ onSend, disabled }) => {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      await onSend(trimmed);
      setText('');
    } finally {
      setSending(false);
    }
  };
  return (
    <form
      onSubmit={submit}
      className="sticky bottom-0 bg-white border-t border-[#e2e8f0] p-3 flex gap-2 items-end pb-[max(0.75rem,env(safe-area-inset-bottom))]"
      data-testid="chat-composer"
    >
      <textarea
        rows={1}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(e); }
        }}
        placeholder="Type a message… (Enter to send, Shift+Enter for newline)"
        className="flex-1 resize-none border border-[#e2e8f0] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb]/40 min-h-[40px] max-h-[120px]"
        disabled={disabled || sending}
        data-testid="chat-input"
      />
      <Button
        type="submit"
        disabled={!text.trim() || sending || disabled}
        className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white h-10 shrink-0 disabled:opacity-60"
        data-testid="send-message-button"
      >
        <Send className="h-4 w-4" />
      </Button>
    </form>
  );
};

const ResidentChat = ({ user }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/chat/messages`, { withCredentials: true });
      if (!aliveRef.current) return;
      setMessages(data);
    } catch (_) {
      if (aliveRef.current) toast.error('Failed to load messages');
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  };

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    load();
    const t = setInterval(load, POLL_MS);
    return () => {
      aliveRef.current = false;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (content) => {
    try {
      await axios.post(`${API}/chat/messages`, { content, recipient_id: null }, { withCredentials: true });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    }
  };

  return (
    <Card className="bg-white border border-[#e2e8f0] flex flex-col h-[calc(100vh-14rem)] md:h-[calc(100vh-12rem)] overflow-hidden" data-testid="resident-chat">
      <div className="p-4 border-b border-[#e2e8f0] flex items-center gap-3 bg-[#f8fafc]">
        <div className="h-10 w-10 rounded-full bg-[#2563eb] text-white flex items-center justify-center">
          <Building2 className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm font-semibold" style={{ fontFamily: 'Outfit' }}>Management Office</p>
          <p className="text-xs text-[#64748b]">Reach the management team about anything.</p>
        </div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#f8fafc]">
        {loading ? (
          <p className="text-center text-sm text-[#64748b]">Loading…</p>
        ) : messages.length === 0 ? (
          <p className="text-center text-sm text-[#94a3b8] py-8">No messages yet — say hello to your management team.</p>
        ) : (
          messages.map((m) => <Bubble key={m._id} msg={m} isMine={m.sender_id === user._id} />)
        )}
      </div>
      <Composer onSend={send} />
    </Card>
  );
};

const ManagementChat = ({ user }) => {
  const [threads, setThreads] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(true);
  const [msgsLoading, setMsgsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const scrollRef = useRef(null);

  const aliveRef = useRef(true);

  const loadThreads = async () => {
    try {
      const { data } = await axios.get(`${API}/chat/threads`, { withCredentials: true });
      if (!aliveRef.current) return;
      setThreads(data);
    } catch (_) {
      if (aliveRef.current) toast.error('Failed to load threads');
    } finally {
      if (aliveRef.current) setThreadsLoading(false);
    }
  };

  const loadMessages = async (rid) => {
    if (!rid) return;
    setMsgsLoading(true);
    try {
      const { data } = await axios.get(`${API}/chat/messages`, {
        params: { with_user: rid },
        withCredentials: true,
      });
      if (!aliveRef.current) return;
      setMessages(data);
    } catch (_) {
      if (aliveRef.current) toast.error('Failed to load messages');
    } finally {
      if (aliveRef.current) setMsgsLoading(false);
    }
  };

  useEffect(() => {
    aliveRef.current = true;
    loadThreads();
    const t = setInterval(loadThreads, POLL_MS);
    return () => {
      aliveRef.current = false;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    loadMessages(selectedId);
    const t = setInterval(() => loadMessages(selectedId), POLL_MS);
    return () => clearInterval(t);
  }, [selectedId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (content) => {
    if (!selectedId) return;
    try {
      await axios.post(`${API}/chat/messages`, { content, recipient_id: selectedId }, { withCredentials: true });
      await loadMessages(selectedId);
      loadThreads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    }
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return threads;
    return threads.filter((t) =>
      (t.resident_name || '').toLowerCase().includes(s) ||
      (t.unit_number || '').toLowerCase().includes(s)
    );
  }, [threads, search]);

  const selectedThread = threads.find((t) => t.resident_id === selectedId);
  const showList = !selectedId; // on mobile we swap between list & thread

  return (
    <Card className="bg-white border border-[#e2e8f0] overflow-hidden grid grid-cols-1 md:grid-cols-[320px_1fr] h-[calc(100vh-14rem)] md:h-[calc(100vh-12rem)]" data-testid="management-chat">
      {/* Threads list */}
      <div className={`border-r border-[#e2e8f0] bg-[#f8fafc] flex flex-col ${showList ? '' : 'hidden md:flex'}`}>
        <div className="p-3 border-b border-[#e2e8f0] bg-white">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94a3b8]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search residents…"
              className="pl-9 h-9"
              data-testid="thread-search-input"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="thread-list">
          {threadsLoading ? (
            <p className="text-center text-sm text-[#64748b] p-4">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-sm text-[#94a3b8] p-4">No residents match.</p>
          ) : (
            filtered.map((t) => (
              <button
                key={t.resident_id}
                type="button"
                onClick={() => setSelectedId(t.resident_id)}
                className={`w-full text-left px-3 py-3 border-b border-[#f1f5f9] hover:bg-white transition-colors ${
                  selectedId === t.resident_id ? 'bg-white' : ''
                }`}
                data-testid={`thread-item-${t.resident_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{t.resident_name}</p>
                    <p className="text-xs text-[#64748b] truncate">
                      {t.unit_number ? `Unit ${t.unit_number} · ` : ''}
                      {t.last_message || 'No messages yet'}
                    </p>
                  </div>
                  {t.unread_count > 0 && (
                    <span className="bg-[#2563eb] text-white text-[10px] font-semibold rounded-full h-5 min-w-[20px] px-1.5 flex items-center justify-center flex-shrink-0" data-testid={`unread-${t.resident_id}`}>
                      {t.unread_count}
                    </span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Thread view */}
      <div className={`flex flex-col bg-[#f8fafc] ${showList ? 'hidden md:flex' : ''}`}>
        {!selectedId ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <MessageSquare className="h-10 w-10 text-[#94a3b8] mb-3" />
            <p className="text-sm text-[#64748b]">Select a resident to open the thread.</p>
          </div>
        ) : (
          <>
            <div className="p-3 border-b border-[#e2e8f0] bg-white flex items-center gap-3">
              <button
                type="button"
                className="md:hidden text-[#64748b] hover:text-[#0f172a] p-1"
                onClick={() => setSelectedId(null)}
                data-testid="close-thread-button"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="h-9 w-9 rounded-full bg-[#e0e7ff] text-[#2563eb] flex items-center justify-center font-semibold">
                {(selectedThread?.resident_name || '?').charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">{selectedThread?.resident_name || 'Resident'}</p>
                <p className="text-xs text-[#64748b] truncate">
                  {selectedThread?.unit_number ? `Unit ${selectedThread.unit_number}` : '—'}
                </p>
              </div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {msgsLoading && messages.length === 0 ? (
                <p className="text-center text-sm text-[#64748b]">Loading…</p>
              ) : messages.length === 0 ? (
                <p className="text-center text-sm text-[#94a3b8] py-8">No messages in this thread yet.</p>
              ) : (
                messages.map((m) => <Bubble key={m._id} msg={m} isMine={m.sender_id === user._id} />)
              )}
            </div>
            <Composer onSend={send} />
          </>
        )}
      </div>
    </Card>
  );
};

export const Chat = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'management';

  return (
    <div className="space-y-6" data-testid="chat-page">
      <div>
        <h1 className="text-4xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>
          {isAdmin ? 'Resident messages' : 'Message the management office'}
        </h1>
        <p className="text-sm text-[#64748b]">
          {isAdmin
            ? 'Reply to any resident from a single inbox — they get an instant push notification.'
            : 'Send a message any time. The management team gets an instant push notification.'}
        </p>
      </div>
      {isAdmin ? <ManagementChat user={user} /> : <ResidentChat user={user} />}
    </div>
  );
};

export default Chat;
